import { Button, Form, notification, Spin, Tooltip, } from "antd";
import { FormComponentProps } from "antd/lib/form";
import DataGrid, { Column, FilterRow, HeaderFilter, Pager, Paging } from "devextreme-react/data-grid";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { PbrCard } from "src/components/common/PbrCard/PbrCard";
import { ServiceContext } from "src/services/provider/Service.provider";
import { PercentageMask } from "src/util/Masks/PercentageMask";
import "./quote.scss";
import { IQuote } from "src/services/interfaces/trade/precificacao/IQuote";
import { formatCurrency } from "src/util/Masks/formatCurrency";
import { AuthContext } from "src/components/context/AuthContext";
import { QuoteStatusEnum } from 'src/services/interfaces/trade/precificacao/QuoteStatusEnum';

interface IUserQuotesTable {
  style?: React.CSSProperties;
  max?: number;
}

function UserQuotesTable(props: IUserQuotesTable & RouteComponentProps<any> & FormComponentProps) {
  const { currentUser } = useContext(AuthContext);
  const { tradeService, quoteService } = useContext(ServiceContext);

  const [data, setData] = useState<IQuote[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGrid()
  }, [])
  async function fetchGrid() {
    setLoading(true);
    try {
      let quotes = await quoteService.getAllByUser();
      if (props.max) {
        quotes = quotes.slice(0, props.max);
      }
      setData(quotes);
    } catch (error) {
      notification.error({
        message: "Falha na consulta",
        description: "Ocorreu um erro durante a consulta. Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  }

  function continueSale(params) {
    setLoading(true);
    const quote: Partial<IQuote> = {
      id: Number(params.data.id)
    };

    tradeService
      .postCreateFromQuote(quote)
      .then(result => {
        props.history.push(`/venda/${result.id}`, {
          trade: result
        });
      })
      .catch((err) => {
        notification.error({
          message: "Erro durante o processo de venda.",
          description: err?.response?.data?.message ?? err?.message ?? err ?? "Não foi possível prosseguir com o processo de venda. Por favor tente novamente, ou entre em contato conosco"
        });
      });
  }

  const canEditQuote = (quote: IQuote) => quote.status !== String(QuoteStatusEnum.Pendente) || currentUser.permissions.canViewPendingQuotes;
  return (
    <div className="quote">
      <Spin spinning={loading}>
        <PbrCard title={`Cotações`} bordered={false} style={{ width: "100%" }}>
          <Form>
            {data && (
              <DataGrid
                dataSource={data}
                allowColumnReordering={true}
                showBorders={true}
                loadPanel={{ enabled: loading }}
                rowAlternationEnabled={true}
                hoverStateEnabled={true}
                columnAutoWidth={true}
              >
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                <Column visible={currentUser.permissions.canCreateTradeFromQuote} caption="#" width={"180px"} alignment={"center"} cellRender={params => (
                  <Button icon="form" onClick={() => props.history.push(`/monitor/precatorios/list/${params.data.precatorio.id}`)} style={{ color: "green" }}>
                    Editar Precatório
                  </Button>
                )}
                />
                <Column caption="#" width={"150px"} alignment={"center"} cellRender={(params) => {
                  return (
                    <Tooltip title={canEditQuote(params.data) ? "Editar Preço" : "Cotação em negociação, entre em contato"}>
                      <Button onClick={() => canEditQuote(params.data) && props.history.push(`/monitor/cotacoes/${params.data.id}`)} disabled={!canEditQuote(params.data)} icon="form" >
                        Editar Preço
                      </Button>
                    </Tooltip>
                  )
                }}
                />
                <Column visible={currentUser.permissions.canCreateTradeFromQuote} caption="#" width={"150px"} alignment={"center"} cellRender={params => (
                  <Button icon="play-circle" onClick={() => continueSale(params)} style={{ color: "green" }}>
                    Iniciar Venda
                  </Button>
                )}
                />
                <Column visible={currentUser.permissions.canViewPendingQuotes} caption="Usuário de criação" dataType="string" alignment={"center"} cellRender={params => (
                  <Tooltip title={`Escritório: ${params.data.user?.officer?.name}`}>
                    {params.data.user.login}
                  </Tooltip>
                )} />

                <Column visible={currentUser.permissions.canViewPendingQuotes} caption="Status" dataType="string" dataField="status" alignment={"center"} />
                <Column caption="ID" dataField="id" dataType="number" width={"100px"} alignment={"center"} />
                <Column caption="Requerente" dataField="precatorio.requester" dataType="string" alignment={"center"} />
                <Column caption="CPF/CNPJ" dataField="precatorio.cpfCnpj" dataType="string" alignment={"center"} />
                <Column caption="Processo" dataField="precatorio.processCode" dataType="string" alignment={"center"} />
                <Column caption="Principal" alignment={"center"} dataField="precatorio.principal" dataType="number" format="string"
                  cellRender={params => {
                    return formatCurrency(params.data.precatorio.principal);
                  }} />
                <Column caption="Valor Venda" alignment={"center"} dataField="pricing.price" dataType="number" format="string"
                  cellRender={params => {
                    return canEditQuote(params.data) ? formatCurrency(params.data.pricing.price) : "Em negociação";
                  }} />

                <Column caption="Comissão" alignment={"center"} dataField="pricing.commissionValue" dataType="number" visible={currentUser.permissions.userCanSeeCommission}
                  cellRender={params => {
                    return canEditQuote(params.data) ? formatCurrency(params.data.pricing?.commission?.officer?.userCommissionCalculated?.value ?? params.data.pricing?.commission?.officer?.full?.value) : "Em negociação";
                  }} />

                <Column caption="% Comissão" alignment={"center"} dataField="pricing.commission" dataType="number" visible={currentUser.permissions.userCanSeeCommission}
                  cellRender={params => {
                    return canEditQuote(params.data) ? PercentageMask(params.data.pricing?.commission?.officer?.userCommissionCalculated?.percentage ?? params.data.pricing?.commission?.officer?.full?.percentage, 2, 2) : "Em negociação";
                  }} />
                <Column caption="Processo Origem" dataField="precatorio.processCodeSource" dataType="string" alignment={"center"} />
                <Column caption="Tribunal" dataField="precatorio.court.name" dataType="string" alignment={"center"} />
                <Column caption="Indexador" dataField="precatorio.correctionType.name" dataType="string" alignment={"center"} />
                <Column caption="Percentual do Credor" alignment={"center"} dataField="pricing.reducedPercentLender" dataType="number" cellRender={params => {
                  return PercentageMask(params.data.pricing.reducedPercentLender === null ? 1 : params.data.pricing.reducedPercentLender, 2, 2);
                }}
                />
                <Column caption="PSS" alignment={"center"} dataField="precatorio.isPss" dataType="boolean" />
                <Pager allowedPageSizes={[10, 25, 50, 100]} showPageSizeSelector={true} />
                <Paging defaultPageSize={20} />
              </DataGrid>
            )}

          </Form>
        </PbrCard>
      </Spin>
    </div>
  );

}

export default withRouter(Form.create()(UserQuotesTable));



// WEBPACK FOOTER //
// ./src/pages/monitor/my-quotes/UserQuotesTable.tsx